import "react-datepicker/dist/react-datepicker.css";
import classes from "./Deliveries.module.css";
import DatePicker from "react-datepicker";
import WhitePlusIcon from "../../images/deliveries/whitePlusIcon.svg";
import { BaseTable } from "../../components/UI/baseTable/BaseTable.jsx";
import { ActionBtn } from "../../components/buttons/actionBtn/ActionBtn.jsx";
import { StatusChip } from "../../components/statusChip/StatusChip.jsx";
import { useNavigate } from "react-router-dom";
import { PageWrapper } from "../../components/UI/pageWrapper/PageWrapper.jsx";
import { CalendarIcon } from "../../images/drivers/calendarIcon.jsx";
import { fetchDrivers } from "../../redux/driver/thunks/getDriverThunk.js";
import { ThreeDotsIcon } from "../../images/threeDotsIcon.jsx";
import { SelectElement } from "../../components/selectElement/SelectElement.jsx";
import { setSearchQuery } from "../../redux/misc/slices/searchSlice.js";
import { fetchCustomers } from "../../redux/customer/thunks/getCustomerThunk.js";
import { fetchDeliveries } from "../../redux/package/thunks/getDeliveryThunk.js";
import { useTranslations } from "../../helperFunctions/customHooks/useTranslations.jsx";
import { ResetFiltersBtn } from "../../components/buttons/resetFiltersBtn/ResetFiltersBtn.jsx";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useRef, useState } from "react";
import { CalendarIcon as DashboardCalendarIcon } from "../../images/dashboard/calendarIcon.jsx";
import {
  onEdit,
  setIsEditModalShown,
  setIsEditMode,
  setIsModalsBgShown,
  setIsWithHistory,
  setSelectedDataForEdit,
  setSelectedDriverInfo,
  setSelectedOrderIndex,
  toggleThreeDotsModal,
} from "../../redux/globalFunctions/globalFnSlice.js";

export function Deliveries() {
  const [currentPage, setCurrentPage] = useState(1);
  const dispatch = useDispatch();
  const isFirstRender = useRef(true);
  const { t } = useTranslations();
  const deliveriesPage = t.deliveries;

  // Variables for redux states.
  const formData = useSelector((state) => state.forms.packageForm);
  const createPackageState = useSelector((state) => state.createDelivery);
  const updatePackageState = useSelector((state) => state.updatePackage);
  const cancelPackageState = useSelector((state) => state.cancelDelivery);
  const deliveriesState = useSelector((state) => state.deliveries);
  const searchQuery = useSelector((state) => state.search.query); // Get search query from Redux state
  const driversState = useSelector((state) => state.drivers);
  const customersState = useSelector((state) => state.customers);

  //Function for checking if some of the filters are present
  const areFiltersActive = (filters) =>
    Object.values(filters).some((value) => value !== "");

  // Function to get the names for drivers and customers.
  useEffect(() => {
    dispatch(fetchDrivers({ names_only: true })); // Get all names
    dispatch(fetchCustomers({ per_page: 1 }));
  }, [dispatch]);

  const [isFiltered, setIsFiltered] = useState(false);

  // State variables for dropdown options and filters
  const [customerOptions, setCustomerOptions] = useState([]);
  const [driverOptions, setDriverOptions] = useState([]);
  const [filters, setFilters] = useState({
    customer_name: "",
    driver_name: "",
    status: "",
    date: "",
  });

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    dispatch(fetchDeliveries({ filters, page: currentPage }));
  }, [currentPage, filters]);

  // Functions for refreshing the results on the page
  useEffect(() => {
    if (createPackageState.status === "success") {
      if (areFiltersActive(filters)) {
        handleResetFilters();
      }
      dispatch(fetchDeliveries({ filters, page: 1 }));
      setCurrentPage(1);
    }
  }, [createPackageState.status]);

  useEffect(() => {
    if (areFiltersActive(filters)) {
      setCurrentPage(1);
      setIsFiltered(true);
    } else {
      setIsFiltered(false);
    }
  }, [filters]);

  useEffect(() => {
    if (
      updatePackageState.status === "success" ||
      cancelPackageState.status === "success"
    ) {
      dispatch(fetchDeliveries({ filters, page: currentPage }));
      setIsFiltered(areFiltersActive(filters));
    }
  }, [dispatch, updatePackageState.status, cancelPackageState.status]);

  useEffect(() => {
    // Update filters with search query
    setFilters({
      ...filters,
      customer_name: searchQuery,
    });
    setCurrentPage(1);
    setIsFiltered(true);
  }, [searchQuery]);

  useEffect(() => {
    // Update dropdown options when deliveries data changes
    if (deliveriesState && deliveriesState.deliveries.length > 0) {
      // Extract unique customer names
      const uniqueCustomers = Array.from(
        new Set(
          customersState.customer_names.map((customer) => customer.c_name)
        )
      );
      setCustomerOptions(uniqueCustomers);

      // Extract unique driver names
      const uniqueDrivers = Array.from(
        new Set(driversState.driver_names.map((driver) => driver.d_name))
      );
      setDriverOptions(uniqueDrivers);
    }
  }, [deliveriesState, driversState, customersState]);

  const currentDeliveries = deliveriesState.deliveries;

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters({ ...filters, [name]: value });
  };

  const handleResetFilters = () => {
    setFilters({
      customer_name: "",
      driver_name: "",
      status: "",
      date: "",
    });
    dispatch(setSearchQuery("")); // Reset search query in Redux state
    setIsFiltered(false);
  };

  function handleAddPackageClick() {
    if (!formData.driver) {
      dispatch(setSelectedDriverInfo(null));
    }
    dispatch(onEdit());
    dispatch(setIsEditMode(false));
  }

  // Getting total pages count functions.
  const totalPages = Math.ceil(deliveriesState.total_count / 10);
  const totalFilteredPages = Math.ceil(deliveriesState.filtered_count / 10);

  const isStatusLoading = deliveriesState.status === "loading";

  const columns = [
    { label: deliveriesPage.table.order },
    { label: deliveriesPage.table.customer },
    { label: deliveriesPage.table.pickupAddress },
    { label: deliveriesPage.table.dropoffAddress },
    { label: deliveriesPage.table.date },
    { label: deliveriesPage.table.driver },
    { label: deliveriesPage.table.createdBy },
    { label: deliveriesPage.table.status },
    { label: deliveriesPage.table.action },
  ];

  const navigate = useNavigate();

  function handleScheduleClick() {
    navigate("/scheduled-deliveries");
  }

  const buttons = [
    <button className={classes.scheduleBtn} onClick={handleScheduleClick}>
      <DashboardCalendarIcon /> <span>{deliveriesPage.schedule}</span>
    </button>,
    <ActionBtn
      onClick={handleAddPackageClick}
      text={deliveriesPage.btnText}
      imgSrc={WhitePlusIcon}
      height={"42px"}
    />,
  ];

  const handleThreeDotsClick = (e, index, data) => {
    const buttonPosition = e.currentTarget.getBoundingClientRect();
    const scrollY = window.scrollY || window.pageYOffset;

    const payload = {
      x: buttonPosition.left + window.scrollX - 55,
      y: buttonPosition.top + scrollY - 60,
    };

    dispatch(setSelectedOrderIndex(index));
    dispatch(toggleThreeDotsModal(payload));
    dispatch(setIsWithHistory(false));
    dispatch(setIsEditMode(true));
    dispatch(setSelectedDataForEdit(data));
  };

  const statusOptions = Object.entries(deliveriesPage.statusOptions).map(
    ([key, label]) => ({
      label,
      value: key,
    })
  );

  function assignDriverClick(data) {
    dispatch(setIsModalsBgShown(true));
    dispatch(setIsEditModalShown(true));
    dispatch(setIsEditMode(true));
    dispatch(setSelectedDataForEdit(data));
  }

  return (
    <PageWrapper buttons={buttons} title={deliveriesPage.deliveries}>
      <BaseTable
        status={deliveriesState.status}
        errorMsg={deliveriesPage.errorFetchingData}
        noItemsMsg={deliveriesPage.noDeliveriesFound}
        items={currentDeliveries}
        columns={columns}
        resultsContainerHeight={"calc(100vh - 350px)"}
        columnAndRowWidth={"130px"}
        filters={
          <>
            {deliveriesPage.filters.filterBy}
            <SelectElement
              name="customer_name"
              id="customerSelect"
              value={filters.customer_name}
              onChange={handleFilterChange}
              placeholder={deliveriesPage.filters.customerSelect}
              options={customerOptions}
            />
            <div className={classes.dateWrapper}>
              <CalendarIcon
                styles={{ position: "absolute", top: "5px", left: "5px" }}
              />
              <DatePicker
                selected={filters.date ? new Date(filters.date) : null}
                onChange={(date) =>
                  handleFilterChange({
                    target: {
                      name: "date",
                      value: date.toLocaleDateString("en-CA"),
                    },
                  })
                }
                placeholderText="Date"
              />
            </div>
            <SelectElement
              name="driver_name"
              id="drivers"
              value={filters.driver_name}
              onChange={handleFilterChange}
              placeholder={deliveriesPage.filters.driversSelect}
              options={driverOptions}
            />
            <SelectElement
              name="status"
              id="statuses"
              value={filters.status}
              onChange={handleFilterChange}
              placeholder={deliveriesPage.filters.statusesSelect}
              options={statusOptions}
              optionLabelKey="label"
              optionValueKey="value"
            />
            <ResetFiltersBtn
              text={deliveriesPage.filters.resetSelectsBtn}
              onClick={handleResetFilters}
              disabled={!areFiltersActive(filters) || isStatusLoading}
            />
          </>
        }
        pagination={true}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        totalPages={totalPages}
        totalFilteredPages={totalFilteredPages}
        isFiltered={isFiltered}
        rows={
          currentDeliveries &&
          currentDeliveries?.map((item, index) => [
            item.package_id,
            item.customer.name,
            item.pickup_address_name,
            item.dropoff_address_name,
            item.order_date,
            item.driver.name ? (
              item.driver.name
            ) : (
              <button
                className={classes.assignBtn}
                onClick={() => assignDriverClick(item)}
              >
                {deliveriesPage.assign}
              </button>
            ),
            item.created_by,
            <StatusChip status={item.status} hoverText={item.status_notes} />,
            <ThreeDotsIcon
              onClick={(e) => handleThreeDotsClick(e, index, item)}
            />,
          ])
        }
        hoverElements={currentDeliveries.map((item) => (
          <div>
            {item.pd_width
              ? `${item.pd_width}x${item.pd_depth}x${
                  item.pd_height
                }m; ${item.pd_volume?.toFixed(3)}m³`
              : "0x0x0cm; 0cm³"}
          </div>
        ))}
      />
    </PageWrapper>
  );
}
